import axios from "axios";
import moment from "moment";
import { createAction } from "redux-actions";
import { takeLatest, call, put, select, all } from "redux-saga/effects";
import { queryParams } from "../../../../utils/queryParams.js";
import { API_DATE_FORMAT } from "../../../../app_constants.js";
import { showToast } from "../../../../utils/showToast";
import { areMissingFilters } from "./serviceBookingsSelectors.js";

export const SET_LOADING = "saga/SET_LOADING";
export const SET_FILTERS_LOADING = "saga/SET_FILTERS_LOADING";
export const SET_HEAT_MAP_LOADING = "saga/SET_HEAT_MAP_LOADING";
export const SET_DATA_POINTS_LOADING = "saga/SET_DATA_POINTS_LOADING";
export const SET_FILTERS_DIRTY = "saga/SET_FILTERS_DIRTY";
export const SET_TABLE_DATA_LOADING = "saga/SET_TABLE_DATA_LOADING";
export const SET_DATES = "saga/SET_DATES";
export const SET_BREAKDOWN_FILTER_LOADING = "saga/SET_BREAKDOWN_FILTER_LOADING";
export const SET_DATES_FOR_FUTURE_BOOKING = "saga/serviceBookings/SET_DATES_FOR_FUTURE_BOOKING";

export const FETCH_CURRENT_USER = "saga/FETCH_CURRENT_USER";
export const FETCH_CURRENT_USER_SUCCESS = "saga/FETCH_CURRENT_USER_SUCCESS";

export const FETCH_CHART_SUCCESS = "saga/FETCH_CHART_SUCCESS";
export const FETCH_GRAPH_SUCCESS = "saga/FETCH_GRAPH_SUCCESS";

export const FETCH_TABLE_DATA = "saga/FETCH_TABLE_DATA";
export const FETCH_TABLE_DATA_SUCCESS = "saga/FETCH_TABLE_DATA_SUCCESS";

export const FETCH_FILTERS_SUCCESS = "saga/FETCH_FILTERS_SUCCESS";
export const FETCH_DATA_POINTS_SUCCESS = "saga/FETCH_DATA_POINTS_SUCCESS";
export const CHANGE_PERCENTAGE_TOGGLE = "saga/CHANGE_PERCENTAGE_TOGGLE";

export const UPDATE_DATES = "saga/UPDATE_DATES";
export const UPDATE_GROUP_BY_FILTER = "saga/UPDATE_GROUP_BY_FILTER";
export const UPDATE_CUSTOM_RANGE = "sagas/UPDATE_CUSTOM_RANGE";
export const UPDATE_BREAKDOWN_CHART_FILTER = "sagas/UPDATE_BREAKDOWN_CHART_FILTER";

export const YEAR_OVER_YEAR_LOADING = "sagas/YEAR_OVER_YEAR_LOADING";
export const UPDATE_CHART = "sagas/UPDATE_CHART";
export const RESET_FILTERS = "saga/RESET_FILTERS";
export const UPDATE_FILTERS = "saga/UPDATE_FILTERS";
export const RESET_DATA = "sagas/RESET_DATA";
export const UPDATE_FUTURE_BOOKINGS = "saga/serviceBookings/UPDATE_FUTURE_BOOKINGS";
export const RESET_BOOKING_STATUS_FILTER_SELECTED =
  "sagas/serviceBookings/RESET_BOOKING_STATUS_FILTER_SELECTED";

export const actionCreators = {
  fetchCurrentUser: createAction(FETCH_CURRENT_USER),
  getTableData: createAction(FETCH_TABLE_DATA),
  updateData: createAction(UPDATE_CHART),
  resetData: createAction(RESET_FILTERS),
  updateDates: createAction(UPDATE_DATES),
  updateCustomRange: createAction(UPDATE_CUSTOM_RANGE),
  onChangePercentageToggle: createAction(CHANGE_PERCENTAGE_TOGGLE),
  updateBreakdownChartFilter: createAction(UPDATE_BREAKDOWN_CHART_FILTER),
  updateGroupBy: createAction(UPDATE_GROUP_BY_FILTER),
  updateFilters: createAction(UPDATE_FILTERS),
  updateFutureBookings: createAction(UPDATE_FUTURE_BOOKINGS)
};

export default function* watchServiceBookings() {
  yield takeLatest(FETCH_CURRENT_USER, getCurrentUserData);
  yield takeLatest(FETCH_TABLE_DATA, fetchModalTableData);
  yield takeLatest(UPDATE_CHART, updateData);
  yield takeLatest(UPDATE_BREAKDOWN_CHART_FILTER, updateBreakdownFilter);
  yield takeLatest(UPDATE_GROUP_BY_FILTER, fetchHeatMapChart);
  yield takeLatest(CHANGE_PERCENTAGE_TOGGLE, fetchHeatMapChart);
  yield takeLatest(RESET_FILTERS, loadModuledData);
  yield takeLatest([UPDATE_DATES, UPDATE_CUSTOM_RANGE], fetchFilters);
  yield takeLatest(UPDATE_FUTURE_BOOKINGS, updateFutureBookings);
}

function* loadModuledData() {
  yield put({ type: SET_LOADING, payload: true });
  yield setDates();
  try {
    yield fetchFilters();
    const allSagas = [fetchHeatMapChart(), fetchBreakdown(), fetchDataPoints()];
    yield all(allSagas);

    yield put({ type: SET_LOADING, payload: false });
    yield put({ type: SET_FILTERS_DIRTY, payload: false });
  } catch (error) {
    console.warn(error);
  }
}

function* updateFutureBookings({ payload }) {
  payload ? yield setDatesForFutureBookings() : yield setDates();
  try {
    yield fetchFilters();
    yield put({ type: RESET_BOOKING_STATUS_FILTER_SELECTED });
  } catch (error) {
    console.warn(error);
  }
}

function* setDatesForFutureBookings() {
  const startDate = yield select((state) => state.serviceBookings.currentUser.calendarEndDate);
  const hourlyImport = yield select((state) => state.serviceBookings.currentUser.hourlyImport);
  const currentDate = hourlyImport
    ? startDate
    : moment(startDate).add(1, "days").format(API_DATE_FORMAT);
  const endDate = moment(currentDate).add(14, "days").startOf("day").format(API_DATE_FORMAT);
  const maxDate = moment(currentDate).add(30, "days").startOf("day").format(API_DATE_FORMAT);
  yield put({
    type: SET_DATES_FOR_FUTURE_BOOKING,
    payload: {
      selectedRequestDates: {
        startDate: currentDate,
        endDate
      },
      minDate: currentDate,
      maxDate
    }
  });
}

function* getCurrentUserData(data) {
  yield put({
    type: FETCH_CURRENT_USER_SUCCESS,
    payload: {
      currentUser: data.payload.currentUser,
      numberOfMonths: data.payload.numberOfMonths
    }
  });
  yield loadModuledData();
}

function* updateData() {
  const allSagas = [fetchHeatMapChart(), fetchBreakdown(), fetchDataPoints()];

  try {
    yield put({ type: SET_FILTERS_DIRTY, payload: false });
    yield all(allSagas);
  } catch (error) {
    console.warn(error);
  }
}

const baseUrl = queryParams.apiVersion + "pages/services/service_bookings";

const apiRequest = (urn, params, method = "post") =>
  axios({
    method,
    url: baseUrl + urn,
    ...(method === "get" ? { params } : { data: params })
  });

const serviceBookingsApi = {
  getFilters: (params) => apiRequest("/get_filters", params, "get"),
  getHeatmap: (params) => apiRequest("/heatmap", params),
  getDataPoints: (params) => apiRequest("/data_points", params),
  getBreakdown: (params) => apiRequest("/breakdown", params),
  getModalData: (params) => apiRequest("/modal_data", params)
};

function* fetchServiceBookingsData({ apiMethod, successType, loadingType, additionalParams = {} }) {
  const { common, ...rest } = yield getApiParams();

  if (
    areMissingFilters({
      visit_names: rest.visit_names,
      membership_types: rest.membership_type_ids,
      future_bookings: common.future_bookings
    })
  )
    return;

  yield put({ type: loadingType, payload: true });
  try {
    const response = yield call(apiMethod, {
      ...common,
      ...rest,
      ...additionalParams
    });

    yield put({ type: successType, payload: response.data });
    yield put({ type: loadingType, payload: false });
  } catch (error) {
    yield put({ type: loadingType, payload: false });
    if (error.response?.status === 422) {
      showToast(error.response.data.error || error.response.data.message, 5000, "error");
    }
    console.error(error);
  }
}

function* updateBreakdownFilter({ payload }) {
  yield put({ type: SET_BREAKDOWN_FILTER_LOADING, payload: payload.filter });
  yield fetchBreakdown();
  yield put({ type: SET_BREAKDOWN_FILTER_LOADING, payload: null });
}

function* fetchBreakdown() {
  const periodType = yield select((state) => state.serviceBookings.selectedFilters.period_type);
  const view = yield select((state) => state.serviceBookings.selectedFilters.view);

  yield call(fetchServiceBookingsData, {
    apiMethod: serviceBookingsApi.getBreakdown,
    successType: FETCH_GRAPH_SUCCESS,
    loadingType: YEAR_OVER_YEAR_LOADING,
    additionalParams: {
      period_type: periodType.name.toLowerCase(),
      view: view.id
    }
  });
}

function* fetchDataPoints() {
  yield call(fetchServiceBookingsData, {
    apiMethod: serviceBookingsApi.getDataPoints,
    successType: FETCH_DATA_POINTS_SUCCESS,
    loadingType: SET_DATA_POINTS_LOADING
  });
}

function* fetchHeatMapChart() {
  const { common, ...rest } = yield getApiParams();

  if (
    areMissingFilters({
      visit_names: rest.visit_names,
      membership_types: rest.membership_type_ids,
      future_bookings: common.future_bookings
    })
  )
    return;

  const {
    selectedFilters: { group_by },
    show_percentage
  } = yield select((state) => state.serviceBookings);

  yield put({ type: SET_HEAT_MAP_LOADING, payload: true });

  try {
    const response = yield call(serviceBookingsApi.getHeatmap, {
      ...common,
      ...rest,
      group_by: group_by.id,
      show_percentage
    });

    yield put({ type: FETCH_CHART_SUCCESS, payload: response.data });
    yield put({ type: SET_HEAT_MAP_LOADING, payload: false });
  } catch (error) {
    yield put({ type: SET_HEAT_MAP_LOADING, payload: false });
    if (error.response?.status === 422) {
      showToast(error.response.data.error || error.response.data.message, 5000, "error");
    }
    console.error(error);
  }
}

function* fetchModalTableData(payload) {
  yield put({ type: SET_TABLE_DATA_LOADING, payload: true });

  const { common, visit_names, membership_type_ids, booking_statuses, staff_uids } =
    yield getApiParams();

  if (
    areMissingFilters({
      visit_names,
      membership_types: membership_type_ids,
      future_bookings: common.future_bookings
    })
  )
    return;

  try {
    const response = yield call(serviceBookingsApi.getModalData, {
      ...common,
      tableDataUid: payload.payload.tableDataUid,
      visit_names,
      membership_type_ids,
      booking_statuses,
      staff_uids
    });

    yield put({ type: FETCH_TABLE_DATA_SUCCESS, payload: response.data });
  } catch (error) {
    yield put({ type: SET_TABLE_DATA_LOADING, payload: false });
    if (error.response?.status === 422) {
      showToast(error.response.data.error || error.response.data.message, 5000, "error");
    }
    console.error(error);
  }
}
function* fetchFilters() {
  let { common } = yield getApiParams();
  yield put({ type: SET_FILTERS_LOADING, payload: true });
  try {
    const response = yield call(serviceBookingsApi.getFilters, common);

    yield put({ type: FETCH_FILTERS_SUCCESS, payload: response.data });
    yield put({ type: SET_FILTERS_LOADING, payload: false });
  } catch (error) {
    yield put({ type: SET_FILTERS_LOADING, payload: false });
    if (error.response?.status === 422) {
      showToast(error.response.data.error || error.response.data.message, 5000, "error");
    }
    console.error(error);
  }
}

function* setDates() {
  const endDate = yield select((state) => state.serviceBookings.currentUser.calendarEndDate);
  const startDate = moment(endDate).startOf("month").format(API_DATE_FORMAT);

  yield put({
    type: SET_DATES,
    payload: {
      startDate,
      endDate
    }
  });
}

function* getApiParams() {
  const { organization_id, short_url } = yield select((state) => state.commonReducer.commonProps);

  const {
    selectedFilters: { visit_names, membership_types, booking_statuses, staff_names },
    selectedRequestDates: { startDate, endDate },
    future_bookings
  } = yield select((state) => state.serviceBookings);

  const params = {
    common: {
      organization_id,
      short_url,
      start_date: startDate,
      end_date: endDate,
      future_bookings
    },
    visit_names: visit_names.map((elem) => elem.name),
    ...(future_bookings ? {} : { membership_type_ids: membership_types.map((elem) => elem.id) }),
    booking_statuses: booking_statuses.map((elem) => elem.id),
    staff_uids: staff_names.map((elem) => elem.id)
  };

  return params;
}
